import { Box, Button } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterDatePicker } from './FilterDatePicker';
import { selectors, actions } from '../../store/dux/field-agrilytics-dux';

export default function ScopedMapControls(props) {
  const { mapScope, onCalendarDate, canOpen, triggerControlChange } = props;
  const dispatch = useDispatch();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date().toISOString());
  const currentScope = useSelector(selectors.mapScope(mapScope));
  const activeScopes = useSelector(selectors.activeScopes);
  const selectedRevision = useSelector(selectors.selectedRevision(mapScope));

  const revisionStartsAt = useMemo(
    () => new Date(selectedRevision?.startsAt),
    [selectedRevision],
  );

  const revisionEndsAt = useMemo(
    () =>
      selectedRevision.endsAt ? new Date(selectedRevision.endsAt) : new Date(),
    [selectedRevision],
  );

  useEffect(() => {
    setCalendarDate(revisionEndsAt);
  }, [revisionEndsAt]);

  useEffect(() => {
    if (!canOpen) setCalendarOpen(false);
  }, [canOpen]);

  return (
    <>
      <Box sx={{ m: 1, position: 'relative' }}>{buildCompareButton()}</Box>
      <Box sx={{ m: 1, position: 'relative' }}>
        <Button
          title="Open Calendar"
          variant="contained"
          color="inherit"
          onClick={handleOpenCalendarClick}
          sx={{
            pointerEvents: 'auto',
            width: 36,
            bgcolor: 'white',
            minWidth: 0,
            mb: 1,
          }}
        >
          <CalendarTodayIcon
            fontSize="medium"
            sx={{
              color: calendarOpen ? 'black' : '#666',
              '&:hover': { color: '#333' },
            }}
          />
        </Button>
        <Box
          sx={{
            display: calendarOpen ? 'flex' : 'none',
            flexDirection: 'column',
            m: 1,
            minWidth: 0,
            bgcolor: 'white',
            pointerEvents: 'auto',
            position: 'absolute',
            right: 0,
            maxHeight: 'calc(100% - 60px)',
          }}
        >
          <FilterDatePicker
            minDate={dayjs(revisionStartsAt).add(1, 'day')}
            maxDate={dayjs(revisionEndsAt)}
            value={new Date(calendarDate).toLocaleDateString()}
            onChange={handleCalendarDateChange}
          />
        </Box>
      </Box>
    </>
  );

  function buildCompareButton() {
    if (mapScope === 'default' && activeScopes.length > 1) return null;
    return (
      <Button
        title="Compare"
        variant="contained"
        color="inherit"
        onClick={handleCompare}
        sx={{
          pointerEvents: 'auto',
          minWidth: 0,
          bgcolor: 'white',
          mb: 1,
          ...(mapScope !== 'default' ? { width: '36px' } : {}),
        }}
        data-testid={'compare-btn'}
      >
        {mapScope === 'default' ? 'Compare' : <CloseIcon />}
      </Button>
    );
  }

  function handleCompare() {
    if (activeScopes.length === 1)
      return dispatch(actions.addScope('comparison', currentScope));
    dispatch(actions.removeScope(mapScope));
  }

  function handleOpenCalendarClick() {
    if (!calendarOpen) triggerControlChange();
    setCalendarOpen(!calendarOpen);
  }

  function handleCalendarDateChange(selected) {
    if (selected < revisionStartsAt || selected > revisionEndsAt) return;
    setCalendarDate(selected.toISOString());
    const [date] = selected.toISOString().split('T');
    const filter = new Date(`${date}T23:59:59Z`);
    onCalendarDate(filter > revisionEndsAt ? revisionEndsAt : filter);
  }
}
